import React, { Suspense, useMemo, useRef } from "react";
import * as THREE from "three";
import { useTexture } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import Avatar from "@/components/scene/Avatar";
import ToonNPC, { NPC_PALETTE } from "@/components/scene/ToonNPC";
import AftermathFX from "@/components/scene/AftermathFX";
import InstancedCity from "@/components/scene/InstancedCity";
import { FireParticles } from "@/components/scene/Hazards";

// --- Liberty Street 3D corridor ---------------------------------------------
// Real geometry: asphalt road + concrete curbs + extruded, photo-textured
// building shells on both sides. The tower + dust wall sit BEHIND the camera
// (+Z); everyone flees WEST, down the street into the vanishing point (-Z).
const ROAD_HALF = 5.5;      // drivable road half-width (D-pad stays within)
const WALL_HALF = 8.2;      // inner building wall (debris/dust clamp)
const Z_NEAR = 8;           // near end (behind runners)
const Z_FAR = -74;          // vanishing point

function dustTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grd.addColorStop(0, "rgba(190,186,176,0.9)");
  grd.addColorStop(0.6, "rgba(160,156,148,0.5)");
  grd.addColorStop(1, "rgba(160,156,148,0)");
  g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

const FACADE_URLS = [
  "/archival/lobby_atrium.png",
  "/archival/elevator_bank.png",
  "/archival/dark_lobby.png",
  "/archival/lobby_street.png",
  "/archival/elevator_hall.png",
];

function Buildings() {
  const texes = useTexture(FACADE_URLS);
  const far = useTexture("/archival/street_bridge.png");
  const slots = useMemo(() => {
    const arr = [];
    let z = Z_NEAR;
    for (let i = 0; z > Z_FAR + 6; i++) {
      const h = 13 + (i % 3) * 7 + ((i * 37) % 5);
      arr.push({ z: z - 4, h, ti: i % FACADE_URLS.length });
      z -= 9;
    }
    return arr;
  }, []);
  const shell = useMemo(() => new THREE.MeshStandardMaterial({ color: 0x4c4f56, roughness: 0.92 }), []);
  return (
    <group>
      {/* far backdrop at the vanishing point (hazy street) */}
      <mesh position={[0, 8, Z_FAR]}>
        <planeGeometry args={[44, 30]} />
        <meshBasicMaterial map={far} toneMapped={false} />
      </mesh>
      {slots.map((s, i) => (
        <group key={i}>
          {/* LEFT shell + textured inner facade (facing +X, toward the road) */}
          <mesh material={shell} position={[-11.5, s.h / 2, s.z]}>
            <boxGeometry args={[9, s.h, 8]} />
          </mesh>
          <mesh position={[-WALL_HALF + 0.05, s.h / 2, s.z]} rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[7.6, s.h]} />
            <meshBasicMaterial map={texes[s.ti]} toneMapped={false} />
          </mesh>
          {/* RIGHT shell + textured inner facade (facing -X) */}
          <mesh material={shell} position={[11.5, s.h / 2, s.z]}>
            <boxGeometry args={[9, s.h, 8]} />
          </mesh>
          <mesh position={[WALL_HALF - 0.05, s.h / 2, s.z]} rotation={[0, -Math.PI / 2, 0]}>
            <planeGeometry args={[7.6, s.h]} />
            <meshBasicMaterial map={texes[(s.ti + 2) % FACADE_URLS.length]} toneMapped={false} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Pavement() {
  return (
    <group>
      {/* asphalt road */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, (Z_NEAR + Z_FAR) / 2]}>
        <planeGeometry args={[ROAD_HALF * 2, Z_NEAR - Z_FAR]} />
        <meshStandardMaterial color={0x2b2c30} roughness={0.95} />
      </mesh>
      {/* faded center line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, (Z_NEAR + Z_FAR) / 2]}>
        <planeGeometry args={[0.22, Z_NEAR - Z_FAR]} />
        <meshBasicMaterial color={0x8a8460} transparent opacity={0.5} />
      </mesh>
      {/* concrete curbs + sidewalks both sides */}
      {[-1, 1].map((s) => (
        <group key={s}>
          <mesh position={[s * (ROAD_HALF + 0.15), 0.12, (Z_NEAR + Z_FAR) / 2]}>
            <boxGeometry args={[0.3, 0.24, Z_NEAR - Z_FAR]} />
            <meshStandardMaterial color={0x6d6a63} roughness={0.9} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[s * (ROAD_HALF + WALL_HALF) / 2 * 1.0 + s * 1.0, 0.03, (Z_NEAR + Z_FAR) / 2]}>
            <planeGeometry args={[WALL_HALF - ROAD_HALF, Z_NEAR - Z_FAR]} />
            <meshStandardMaterial color={0x53514b} roughness={0.95} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// The collapsing tower + rolling ash wall live BEHIND the camera and surge -Z.
function CollapseBehind({ getRoar }) {
  const twr = useRef();
  const grp = useRef();
  const tex = useMemo(dustTexture, []);
  const puffs = useMemo(() => {
    const a = [];
    for (let i = 0; i < 90; i++) a.push({ x: (Math.random() - 0.5) * 22, y: 0.5 + Math.random() * 22, s: 12 + Math.random() * 16, o: Math.random() * 6 });
    return a;
  }, []);
  useFrame((state) => {
    const r = getRoar();
    if (twr.current) {
      const collapse = Math.max(0, (r - 0.4) / 0.6);
      twr.current.scale.y = 1 - collapse * 0.9;
      twr.current.position.y = 16 - collapse * 14;
    }
    if (grp.current) {
      const roll = Math.max(0, (r - 0.25) / 0.75);
      grp.current.visible = roll > 0.01;
      // starts behind the camera (+Z) and rolls down the street (-Z) overtaking everyone
      grp.current.position.z = 16 - roll * 70;
      grp.current.children.forEach((c, i) => {
        const p = puffs[i];
        c.position.x = Math.max(-WALL_HALF, Math.min(WALL_HALF, p.x + Math.sin(state.clock.elapsedTime * 0.6 + p.o) * 2.2));
        c.position.y = p.y + Math.sin(state.clock.elapsedTime * 0.4 + p.o) * 0.9;
        c.material.opacity = Math.min(0.98, roll * 1.3);
      });
    }
  });
  return (
    <group>
      {/* tower base looming behind the camera */}
      <mesh ref={twr} position={[-3, 16, 20]}>
        <boxGeometry args={[7, 34, 7]} />
        <meshStandardMaterial color={0x9a9c94} roughness={0.9} />
      </mesh>
      <group ref={grp} visible={false}>
        {puffs.map((p, i) => (
          <sprite key={i} position={[p.x, p.y, 0]} scale={[p.s, p.s, 1]}>
            <spriteMaterial map={tex} transparent opacity={0} depthWrite={false} color={0xb6b0a3} />
          </sprite>
        ))}
      </group>
    </group>
  );
}

export default function ArrivalScene({ game, audio }) {
  const crowdRefs = useRef([]);
  const stanleyRef = useRef();
  const brianRef = useRef();
  const heroZ = useRef(0);
  const fogRef = useRef();
  const revealRef = useRef(0);

  // 50 hero runners, lane-spread, all fleeing -Z
  const runners = useMemo(
    () =>
      Array.from({ length: 50 }, (_, i) => {
        const baseX = (((i * 7) % 9) - 4) * 1.05 + (Math.random() - 0.5) * 0.6;
        return { x: baseX, baseX, z: Z_NEAR - (i / 50) * (Z_NEAR - Z_FAR), speed: 1.0 + Math.random() * 0.7, pal: i % NPC_PALETTE.length, scale: 0.9 + Math.random() * 0.24 };
      }),
    []
  );

  // --- Chaos Engine: pooled, randomized hazards (different every run) ---
  const fireRefs = useRef([]);
  const fireLightRefs = useRef([]);
  const debrisRefs = useRef([]);
  const glassRefs = useRef([]);
  const chaos = useRef({
    fires: Array.from({ length: 6 }, () => ({ x: 0, z: -99, ttl: 0, active: false })),
    debris: Array.from({ length: 10 }, () => ({ x: 0, z: 0, y: 20, vy: 0, active: false })),
    glass: Array.from({ length: 4 }, () => ({ x: 0, y: 0, z: 0, t: 0, active: false })),
    spawn: 1.5,
  }).current;

  const getRoar = () => (game.current.roar != null ? game.current.roar : 0.3);

  useFrame((state, dt) => {
    const roar = game.current.roar != null ? game.current.roar : 0;
    const evac = !!game.current.evac;
    revealRef.current = Math.min(1, revealRef.current + dt / 6);
    if (fogRef.current) fogRef.current.density = 0.012 + revealRef.current * 0.016 + roar * 0.03;

    // ---- spawn randomized hazards during the escape ----
    if (evac) {
      chaos.spawn -= dt;
      if (chaos.spawn <= 0) {
        chaos.spawn = 0.8 + Math.random() * 2.2;
        const roll = Math.random();
        if (roll < 0.4) {
          const f = chaos.fires.find((x) => !x.active);
          if (f) { f.active = true; f.x = (Math.random() - 0.5) * ROAD_HALF * 1.7; f.z = -6 - Math.random() * 34; f.ttl = 4 + Math.random() * 5; audio && audio.fireCrackle(); }
        } else if (roll < 0.78) {
          const d = chaos.debris.find((x) => !x.active);
          if (d) { d.active = true; d.x = (Math.random() - 0.5) * (WALL_HALF * 1.4); d.z = -4 - Math.random() * 36; d.y = 14 + Math.random() * 8; d.vy = 5 + Math.random() * 7; }
        } else {
          const gsl = chaos.glass.find((x) => !x.active);
          if (gsl) { gsl.active = true; gsl.x = (Math.random() < 0.5 ? -1 : 1) * (WALL_HALF - 0.4); gsl.y = 3 + Math.random() * 8; gsl.z = -4 - Math.random() * 32; gsl.t = 0; audio && audio.glassShatter(); }
        }
      }
    }
    // update fires
    chaos.fires.forEach((f, i) => {
      const g = fireRefs.current[i];
      const l = fireLightRefs.current[i];
      if (f.active) { f.ttl -= dt; if (f.ttl <= 0) f.active = false; }
      if (g) { g.visible = f.active; g.position.set(f.x, 0, f.z); }
      if (l) { l.visible = f.active; l.position.set(f.x, 1.2, f.z); l.intensity = f.active ? 2.2 + Math.sin(state.clock.elapsedTime * 12 + i) * 0.6 : 0; }
    });
    // update falling debris (ceiling collapse)
    chaos.debris.forEach((d, i) => {
      const m = debrisRefs.current[i];
      if (d.active) { d.y -= d.vy * dt * (0.7 + roar); if (d.y <= 0.25) { d.active = false; audio && audio.debrisThud(); } }
      if (m) {
        m.visible = d.active;
        m.position.set(THREE.MathUtils.clamp(d.x, -WALL_HALF, WALL_HALF), Math.max(0.25, d.y), d.z);
        m.rotation.x += dt * 3; m.rotation.z += dt * 2;
      }
    });
    // update glass bursts (shards scatter then fall)
    chaos.glass.forEach((gs, i) => {
      const grp = glassRefs.current[i];
      if (gs.active) { gs.t += dt; if (gs.t > 1.4) gs.active = false; }
      if (grp) {
        grp.visible = gs.active;
        grp.position.set(gs.x, gs.y - gs.t * gs.t * 3.5, gs.z);
        grp.children.forEach((c, k) => { c.rotation.x += dt * 6; c.rotation.y += dt * 5; c.position.x = Math.sin(k * 2.1) * gs.t * 1.6; c.position.y = Math.cos(k * 1.7) * gs.t * 1.2; });
      }
    });

    // ---- NPC pathfinding: flee -Z, steer from fire, dodge debris, stay on road ----
    runners.forEach((c, i) => {
      const ref = crowdRefs.current[i];
      if (!ref) return;
      if (evac) {
        let steer = 0;
        for (const f of chaos.fires) {
          if (!f.active) continue;
          const dz = c.z - f.z, dx = c.x - f.x;
          if (Math.abs(dz) < 6 && Math.abs(dx) < 3.4) steer += Math.sign(dx || (Math.random() - 0.5)) * (3.4 - Math.abs(dx)) * 0.8;
        }
        for (const d of chaos.debris) {
          if (!d.active || d.y > 5) continue;
          const dz = c.z - d.z, dx = c.x - d.x;
          if (Math.abs(dz) < 3 && Math.abs(dx) < 2.2) steer += Math.sign(dx || (Math.random() - 0.5)) * (2.2 - Math.abs(dx)) * 1.1;
        }
        // ease back toward lane when clear
        c.x += (steer * dt * 3.2) + (c.baseX - c.x) * dt * 0.8;
        c.x = THREE.MathUtils.clamp(c.x, -ROAD_HALF + 0.4, ROAD_HALF - 0.4);
        c.z -= dt * (6 + roar * 4) * c.speed; // west, never turning back
        // recycle well BEFORE the backdrop so no runner ever stands against it
        if (c.z < Z_FAR + 12) { c.z = Z_NEAR + Math.random() * 3; c.x = c.baseX; }
        ref.position.set(c.x, 0, c.z);
        ref.rotation.y = Math.PI; // backs to camera, facing the escape
      } else {
        // idle: keep runners on the road (same X-clamp as evac) and short of the backdrop
        const ix = THREE.MathUtils.clamp(c.baseX, -ROAD_HALF + 0.4, ROAD_HALF - 0.4);
        const iz = Math.max(c.z, Z_FAR + 12);
        ref.position.set(ix, 0, iz);
        ref.rotation.y = Math.PI;
      }
    });

    // survivors lead the escape (backs to camera) but stay mid-street & readable.
    // During the final exchange (Stanley: "…going to fall down" → Brian's reply)
    // they STOP and turn face-to-face; on "Run! We have to move!" (line 72) they
    // turn back downstreet and resume the sprint.
    const lo = game.current.lineOrder;
    const talk = evac && (lo === 70 || lo === 71);
    game.current.heroTalk = talk;
    if (evac && !talk) heroZ.current = Math.max(-6, heroZ.current - dt * 1.6);
    else if (!evac) heroZ.current = 0;
    if (stanleyRef.current) {
      stanleyRef.current.position.z = 2 + heroZ.current;
      stanleyRef.current.rotation.y = THREE.MathUtils.damp(stanleyRef.current.rotation.y, talk ? Math.PI / 2 : Math.PI, 6, dt);
    }
    if (brianRef.current) {
      brianRef.current.position.z = 2 + heroZ.current;
      brianRef.current.rotation.y = THREE.MathUtils.damp(brianRef.current.rotation.y, talk ? (3 * Math.PI) / 2 : Math.PI, 6, dt);
    }
  });

  return (
    <group>
      <fogExp2 ref={fogRef} attach="fog" args={[0xb9b3a6, 0.04]} />
      <ambientLight color={0xece3ce} intensity={1.25} />
      <directionalLight color={0xfff1d6} intensity={1.3} position={[6, 12, 8]} />
      <hemisphereLight args={[0xe0dacc, 0x2a2a26, 0.75]} />

      <Pavement />
      <Suspense fallback={null}>
        <Buildings />
      </Suspense>

      {/* distant skyline over the rooftops */}
      <InstancedCity />

      <CollapseBehind getRoar={getRoar} />
      <AftermathFX getDensity={getRoar} />

      {/* --- Chaos Engine pooled hazards --- */}
      {chaos.fires.map((_, i) => (
        <group key={`fire${i}`} ref={(el) => (fireRefs.current[i] = el)} visible={false}>
          <FireParticles count={50} scale={1.1} intensity={1.2} />
        </group>
      ))}
      {chaos.fires.map((_, i) => (
        <pointLight key={`fl${i}`} ref={(el) => (fireLightRefs.current[i] = el)} color={0xff7a2a} intensity={0} distance={9} decay={2} visible={false} />
      ))}
      {chaos.debris.map((_, i) => (
        <mesh key={`db${i}`} ref={(el) => (debrisRefs.current[i] = el)} visible={false}>
          <boxGeometry args={[0.6 + (i % 3) * 0.3, 0.5, 0.6]} />
          <meshStandardMaterial color={0x8d8880} roughness={1} />
        </mesh>
      ))}
      {chaos.glass.map((_, i) => (
        <group key={`gl${i}`} ref={(el) => (glassRefs.current[i] = el)} visible={false}>
          {new Array(6).fill(0).map((__, k) => (
            <mesh key={k}>
              <tetrahedronGeometry args={[0.12]} />
              <meshStandardMaterial color={0xbfe6f0} roughness={0.1} metalness={0.3} transparent opacity={0.8} />
            </mesh>
          ))}
        </group>
      ))}

      {/* survivors — high-fidelity likeness leading the run (backs to camera) */}
      <group ref={stanleyRef} position={[-0.9, 0, 2]} rotation={[0, Math.PI, 0]}>
        <Avatar which="stanley" url="/models/stanley.glb" faceSign={1} rim rig={{ base: "/models/stanley_rig.glb", run: "/models/stanley_rig.glb" }} getRunning={() => (game.current.evac && !game.current.heroTalk)} getMotion={() => (game.current.evac && !game.current.heroTalk ? "run" : "idle")} />
      </group>
      <group ref={brianRef} position={[0.9, 0, 2]} rotation={[0, Math.PI, 0]}>
        <Avatar which="brian" url="/models/brian.glb" faceSign={-1} rim rig={{ base: "/models/brian_rig.glb", run: "/models/brian_run.glb" }} getRunning={() => (game.current.evac && !game.current.heroTalk)} getMotion={() => (game.current.evac && !game.current.heroTalk ? "run" : "idle")} />
      </group>

      {/* 50 full-limb runners fleeing west down Liberty Street */}
      {runners.map((c, i) => (
        <group key={i} ref={(el) => (crowdRefs.current[i] = el)}>
          <ToonNPC pose="walk" speed={9} scale={c.scale} getRun={() => (game.current.evac ? 1 : 0)} {...NPC_PALETTE[c.pal]} />
        </group>
      ))}
    </group>
  );
}
